import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sparkle, EnvelopeSimple, Copy, ArrowSquareOut } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { toast } from "sonner";

export const ComposeEmailDialog = ({ open, onOpenChange, physicianId, autoStageMatch }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [physician, setPhysician] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [to, setTo] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  // Load templates and physician once dialog opens
  useEffect(() => {
    if (!open || !physicianId) return;
    (async () => {
      const [tpls, p] = await Promise.all([
        api.get("/templates"),
        api.get(`/physicians/${physicianId}`),
      ]);
      setTemplates(tpls.data);
      setPhysician(p.data);
      setTo(p.data.email || "");

      // Auto-pick template based on followup label match
      let initialId = tpls.data[0]?.id;
      if (autoStageMatch) {
        const match = tpls.data.find(t => t.stage_match && autoStageMatch.toLowerCase().includes(t.stage_match.toLowerCase()));
        if (match) initialId = match.id;
      }
      if (initialId) setSelectedTpl(initialId);
    })();
  }, [open, physicianId, autoStageMatch]);

  // When template changes, render it
  useEffect(() => {
    if (!selectedTpl || !physicianId) return;
    api.get(`/templates/${selectedTpl}/render`, { params: { physician_id: physicianId } })
      .then(r => {
        setSubject(r.data.subject);
        setBody(r.data.body);
        if (r.data.to) setTo(r.data.to);
      });
  }, [selectedTpl, physicianId]);

  const personalizeWithAI = async () => {
    setAiLoading(true);
    try {
      const r = await api.post("/templates/ai-draft", {
        physician_id: physicianId,
        base_subject: subject,
        base_body: body,
        tone: "professional",
      });
      setSubject(r.data.subject);
      setBody(r.data.body);
      toast.success("AI personalized");
    } catch (e) {
      toast.error("AI draft failed");
    } finally { setAiLoading(false); }
  };

  const buildMailto = () => {
    const params = new URLSearchParams();
    if (subject) params.set("subject", subject);
    if (body) params.set("body", body);
    return `mailto:${encodeURIComponent(to || "")}?${params.toString()}`;
  };

  const openInMailApp = () => {
    if (!to) {
      toast.warning("No recipient email — opening anyway");
    }
    window.location.href = buildMailto();
  };

  const copyAll = async () => {
    const text = `To: ${to}\nSubject: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-none border-[#E2E2E5]" data-testid="compose-email-dialog">
        <DialogHeader className="border-b border-[#E2E2E5] pb-3">
          <DialogTitle className="font-display font-black tracking-tight text-2xl flex items-center gap-2">
            <EnvelopeSimple size={22} weight="regular" className="text-[#006CFF]" />
            Compose Email{physician ? ` — ${physician.full_name}` : ""}
          </DialogTitle>
          <DialogDescription className="sr-only">Pre-filled email template that opens in your default mail client.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-[180px_1fr] gap-4 py-2 max-h-[65vh] overflow-y-auto">
          <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] pt-2">Template</Label>
          <Select value={selectedTpl} onValueChange={setSelectedTpl}>
            <SelectTrigger data-testid="compose-template-select" className="rounded-none"><SelectValue placeholder="Choose a template" /></SelectTrigger>
            <SelectContent>
              {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>

          <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] pt-2">To</Label>
          <Input data-testid="compose-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@email.com" className="rounded-none font-mono text-sm" />

          <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] pt-2">Subject</Label>
          <Input data-testid="compose-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-none" />

          <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] pt-2">Body</Label>
          <Textarea data-testid="compose-body" value={body} onChange={(e) => setBody(e.target.value)} rows={14} className="rounded-none font-mono text-sm leading-relaxed" />
        </div>

        <DialogFooter className="border-t border-[#E2E2E5] pt-3 gap-2 sm:justify-between">
          <Button
            variant="outline"
            data-testid="compose-ai-btn"
            onClick={personalizeWithAI}
            disabled={aiLoading || !physicianId}
            className="rounded-none gap-1.5 border-[#FF9900] text-[#FF9900] hover:bg-[#FF990010]"
          >
            <Sparkle size={14} weight="fill" />
            {aiLoading ? "DRAFTING..." : "Personalize with AI"}
          </Button>

          <div className="flex gap-2">
            <Button variant="outline" data-testid="compose-copy-btn" onClick={copyAll} className="rounded-none gap-1.5">
              <Copy size={14} weight="regular" /> Copy
            </Button>
            <Button data-testid="compose-open-btn" onClick={openInMailApp} className="bg-[#006CFF] hover:bg-[#0052CC] text-white rounded-none gap-1.5">
              <ArrowSquareOut size={14} weight="bold" /> Open in Email App
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
